/**
 * How big the text is on this device: Normal, Large or Extra large. Like the colour
 * theme (`theme.ts`) it is a per-device preference — never saved with a game, never
 * affecting anyone else's screen, never touching scoring.
 *
 * It works by scaling the page's root font size (a `data-text-size` attribute on <html>,
 * which style.css turns into 100% / 120% / 140%). Almost every size in the stylesheet is in
 * `rem`, so buttons, tiles, spacing and text all scale together and nothing has to be
 * resized one piece at a time. The printed page and the PDF report are unaffected.
 */

export type TextSize = 'normal' | 'large' | 'xlarge';

export interface TextSizeInfo {
  id: TextSize;
  name: string;
  /** The size of the "Aa" sample on the picker button, in pixels (fixed, so the three samples always differ). */
  samplePx: number;
}

export const TEXT_SIZES: TextSizeInfo[] = [
  { id: 'normal', name: 'Normal', samplePx: 16 },
  { id: 'large', name: 'Large', samplePx: 20 },
  { id: 'xlarge', name: 'Extra large', samplePx: 24 },
];

export const TEXT_SIZE_KEY = 'mahjong-scorer:textSize';
const DEFAULT_TEXT_SIZE: TextSize = 'normal';

export function isTextSize(value: unknown): value is TextSize {
  return TEXT_SIZES.some((t) => t.id === value);
}

export function readTextSize(): TextSize {
  try {
    const stored = localStorage.getItem(TEXT_SIZE_KEY);
    return isTextSize(stored) ? stored : DEFAULT_TEXT_SIZE;
  } catch {
    return DEFAULT_TEXT_SIZE;
  }
}

export function writeTextSize(size: TextSize): void {
  try {
    localStorage.setItem(TEXT_SIZE_KEY, size);
  } catch {
    // The size still applies for this visit; it just won't be remembered.
  }
}

/** Paints <html> with the chosen size. Normal removes the attribute so the stylesheet's plain default applies. */
export function applyTextSize(size: TextSize): void {
  const root = document.documentElement;
  if (size === 'normal') delete root.dataset['textSize'];
  else root.dataset['textSize'] = size;
}
